import { planConfigs } from "@postdaily/api/services/plan-limits";
import { xPostsPerMonth } from "@postdaily/api/services/x-credit-rates";

import { joinWithAnd } from "./join-with-and";
import { type CatalogPlan, planPriceSummary } from "./plan-prices";
import { inReviewSentence, platformLabels } from "./platforms";

type PricingFaq = { q: string; a: string };

/**
 * The supported-networks answer is derived from `PLATFORM_CATALOG` rather than
 * written out, because a hand-maintained list here went stale the moment an
 * adapter shipped — it still named three live networks long after seven were
 * connectable. Flipping a catalog entry to `live` now moves it across on its
 * own, exactly as it does on the landing FAQ.
 */
const LIVE_LABELS = platformLabels("live");
const SOON_LABELS = platformLabels("soon");

const IN_REVIEW = inReviewSentence();

const NETWORKS_ANSWER =
  (SOON_LABELS.length > 0
    ? `${joinWithAnd(LIVE_LABELS)} are live today, with ${joinWithAnd(SOON_LABELS)} rolling out. New networks are added to your plan at no extra cost.`
    : `${joinWithAnd(LIVE_LABELS)} are live today, and new networks are added to your plan at no extra cost.`) +
  (IN_REVIEW ? ` ${IN_REVIEW}` : "");

/**
 * The X allowance, quoted from the credit budgets the meter actually enforces.
 *
 * It used to promise "40 posts on Starter, 400 on Pro and 2,000 on Agency",
 * but the cap is a spend budget: a post costs its create (13x more with a
 * link) plus the metric reads that follow it. The promised figures were two to
 * three times what Pro and Agency budgets buy.
 */
const X_ALLOWANCE = (["starter", "pro", "agency"] as const).map((code) => ({
  name: planConfigs[code].name,
  ...xPostsPerMonth(planConfigs[code].limits.maxXCreditsPerMonth),
}));

const [X_FIRST, ...X_REST] = X_ALLOWANCE;

const X_ANSWER = X_FIRST
  ? `X is the one network that charges per post through its API, so it isn't on the Free plan, and each paid plan includes a monthly X budget: about ${X_FIRST.plain} posts on ${X_FIRST.name}, ${joinWithAnd(X_REST.map((plan) => `${plan.plain} on ${plan.name}`))}. A post with a link costs X about 13 times as much, so a month of link posts covers about ${joinWithAnd(X_ALLOWANCE.map((plan) => `${plan.withLink} on ${plan.name}`))}, and each post in a thread counts on its own. Your billing page shows what is left. The other ${LIVE_LABELS.length - 1} networks have no such cap.`
  : "";

/** Pricing page FAQs — shared by the UI and FAQPage JSON-LD. */
export const PRICING_FAQS: readonly PricingFaq[] = [
  {
    q: "Can I change plans later?",
    a: "Absolutely. Upgrade, downgrade or cancel anytime from your billing settings — changes are prorated automatically, so you only ever pay for what you use.",
  },
  {
    q: "What counts as a channel?",
    a: "A channel is a single connected social account — for example, one Bluesky profile or one Facebook Page. Connect as many networks as your plan allows.",
  },
  {
    q: "Is there really a free plan?",
    a: "Yes. The Free plan is free forever — no trial clock, no credit card. It is perfect for getting your first posts out the door on a single channel (X / Twitter is paid-only).",
  },
  {
    q: "How does the free trial work?",
    a: "Annual plans come with a 7-day free trial with full access to that plan — we will remind you before it ends, and you can cancel anytime before then so you are never charged for a plan you did not want. Monthly plans skip the trial and start right away, and you can always evaluate on the Free plan first.",
  },
  {
    q: "What do I save with annual billing?",
    a: "Annual billing gives you two months free versus paying monthly. You can switch between monthly and annual at any time from billing settings.",
  },
  {
    q: "Which networks are supported?",
    a: NETWORKS_ANSWER,
  },
  {
    q: "Are there limits on X (Twitter) posts?",
    a: X_ANSWER,
  },
];

/**
 * The pricing page's FAQs, led by what each plan costs when the live catalog
 * is at hand.
 *
 * The page renders this list and the route's FAQPage JSON-LD is built from it,
 * both from the same catalog read, so the structured data never answers a
 * question the page does not show. Without paid prices (no catalog yet, or
 * Polar unreachable when it was read) the cost question is left out rather
 * than answered without them.
 */
export function pricingFaqs(
  plans: readonly CatalogPlan[] | null | undefined,
): readonly PricingFaq[] {
  const summary = plans ? planPriceSummary(plans) : null;

  return summary
    ? [{ q: "How much does PostDaily cost?", a: summary }, ...PRICING_FAQS]
    : PRICING_FAQS;
}
